import {inject, Injectable} from "@angular/core";
import {SocketIoService} from "./socket-io.service";
import {AlertPopupNotificationResponse} from "../interfaces/websocket/inbound/AlertPopupNotificationResponse";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {EventName} from "../interfaces/websocket/EventName";
import {AppEventBus} from "../event-buses/app.event-bus";
import {AppEvent} from "../event-buses/AppEvent";
import {AppEventName} from "../event-buses/AppEventName";
import {AlertPopupNotificationRequest} from "../interfaces/websocket/outbound/AlertPopupNotificationRequest";
import {AlertInstance} from "../interfaces/domain/alert/AlertInstance";
import {Asset} from "../interfaces/rest/Asset";
import {AlertRuleHistoryRepository} from "../repositories/alert-rule-history.repository";
import {AssetRepository} from "../repositories/asset.repository";
import {LineRepository} from "../repositories/line.repository";

@Injectable({
  providedIn: 'root'
})
export class AlertNotificationWebsocketService {

  private readonly userSessionRepository = inject(UserSessionRepository);
  private readonly alertRuleHistoryRepository = inject(AlertRuleHistoryRepository);
  private readonly assetRepository = inject(AssetRepository);
  private readonly lineRepository = inject(LineRepository);

  private readonly socketIoService = inject(SocketIoService);
  private readonly appEventBus = inject(AppEventBus);

  private listenerRegistered = false;

  constructor() {
    this.socketIoService.getReadyObservable().subscribe((ready) => {
      if(ready) {
        this.onReady();
      }
      else {
        this.onNotReady();
      }
    });
  }

  private onReady(): void {
    if(!this.listenerRegistered) {
      this.socketIoService.on(EventName.Inbound.alertPopupNotificationResponse, (data: any) => {
        this.handleResponse(data);
      });

      this.subscribeCurrentUser();

      this.listenerRegistered = true;
    }
  }

  private onNotReady(): void {
    this.socketIoService.removeAllListeners(EventName.Inbound.alertPopupNotificationResponse);
    this.listenerRegistered = false;
  }

  private handleResponse(data: any): void {
    console.debug(`Received alert popup notification response: ${JSON.stringify(data)}`);

    const response = AlertPopupNotificationResponse.fromObject(data);

    if(response.isDeviceAlert()) {
      const alertInstance = this.createDeviceAlertInstance(response);

      if(alertInstance.state === 'OPEN') {
        if(alertInstance.source && alertInstance.source.length > 0) {
          this.dispatchAlertInstance(alertInstance);
        }
        else {
          this.alertRuleHistoryRepository.getAlertInstance(alertInstance.id).subscribe({
            next: (alertData) => {
              if(alertData.asset) {
                this.assetRepository.getAssetByUniqueAssetIdThingWorksFk(alertData.asset).subscribe({
                  next: (asset) => {
                    alertInstance.source = asset.companyName! + ' > ';
                    alertInstance.source += (asset.plantName! + ' > ');
                    alertInstance.source += (asset.lineName! + ' > ');
                    alertInstance.source += Asset.getFriendlyDisplayName(asset) || '';
                    this.dispatchAlertInstance(alertInstance);
                  },
                  error: (error) => {
                    this.dispatchAlertInstance(alertInstance);
                  }
                });
              }
              else if(alertData.lineId) {
                this.lineRepository.getLine(alertData.lineId).subscribe({
                  next: (line) => {
                    alertInstance.source = line.companyName! + ' > ';
                    alertInstance.source += (line.plant!.plantName! + ' > ');
                    alertInstance.source += line.lineName || '';
                    this.dispatchAlertInstance(alertInstance);
                  },
                  error: (error) => {
                    this.dispatchAlertInstance(alertInstance);
                  }
                })
              }
              else {
                this.dispatchAlertInstance(alertInstance);
              }
            },
            error: (error) => {
              this.dispatchAlertInstance(alertInstance);
            }
          });
        }
      }
      else {
        this.dispatchAlertInstance(alertInstance);
      }
    }
    else {
      console.warn('Received unsupported alert type');
    }
  }

  private dispatchAlertInstance(alertInstance: AlertInstance): void {
    this.appEventBus.dispatch(new AppEvent(AppEventName.AlertNotification, {
      alert: alertInstance,
    }));
  }

  private createDeviceAlertInstance(response: AlertPopupNotificationResponse): AlertInstance {
    const alertInstance: AlertInstance = {
      id: response.uniqueId,
      timestamp: new Date().getTime(),
      state: response.isOpen() ? 'OPEN' : (response.isAcknowledge() ? 'ACKNOWLEDGE' : 'CLOSE'),
      severity: 'HIGH',
      type: 'DEVICE_ALERT',
      title: response.alertTitle,
      description: response.alertDescription,
      source: '',
      response: response,
    };

    return alertInstance;
  }

  private subscribeCurrentUser(): void {
    const currentUser = this.userSessionRepository.getUserSession();

    if(!currentUser) {
      console.warn('NO USER SESSION AVAILABLE FOR ALERT SUBSCRIPTIONS');
    }
    else {
      const request = new AlertPopupNotificationRequest(currentUser.email!);
      this.socketIoService.emit(EventName.Outbound.alertPopupNotificationRequest, request);
      console.log(`Subscribing user ${currentUser.email} to receive alert notifications`);
    }
  }
}
